import { useCallback, useMemo, useRef, useEffect, useState } from 'react'
import { useTimelineItemResize } from 'hooks/timeline'
import {
  convertMediaItemsToTimelineItems,
  convertTimelineItemsToMediaItems
} from 'utils/timeline'
import { clampFadeEffectsToDuration } from 'utils/audio'
import { IMediaItem } from 'types/media'
import { dispatch } from 'store'
import { editorThunks } from 'store/features/editor/thunks'
import { projectDataThunks } from 'store/features/projectData/thunks'

export const useVisualMediaItemResize = ({
  itemId,
  lineIndex,
  mediaItems,
  updateMediaItems
}: {
  itemId: string
  lineIndex: number
  mediaItems: IMediaItem[]
  updateMediaItems: (mediaItems: IMediaItem[]) => void
}) => {
  const [isResizing, setIsResizing] = useState(false)
  const mediaItemsRef = useRef<IMediaItem[]>(mediaItems)

  const timelineItemsInSameLine = useMemo(() => {
    const mediaItemsInSameLine = mediaItems.filter(
      item => item.zIndex === lineIndex
    )
    return convertMediaItemsToTimelineItems(mediaItemsInSameLine)
  }, [mediaItems, lineIndex])

  const {
    items: timelineItems,
    handleLeftResize,
    handleRightResize
  } = useTimelineItemResize(timelineItemsInSameLine, isResizing)

  // update media items
  useEffect(() => {
    updateMediaItems(
      convertTimelineItemsToMediaItems(timelineItems, mediaItems)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineItems])

  mediaItemsRef.current = useMemo(() => {
    return convertTimelineItemsToMediaItems(timelineItems, mediaItems)
  }, [timelineItems, mediaItems])

  const onResizeStart = useCallback(() => {
    setIsResizing(true)
    dispatch(editorThunks.setIsTimelineItemResizing(true))
  }, [])

  const onResizeEnd = useCallback(() => {
    setIsResizing(false)
    dispatch(editorThunks.setIsTimelineItemResizing(false))
    // Trimming changes duration → a fade may now exceed half the clip; clamp it.
    dispatch(
      projectDataThunks.setMediaItems({
        value: mediaItemsRef.current.map(item => ({
          ...item,
          meta: {
            ...item.meta,
            audioEffects: clampFadeEffectsToDuration(
              item.meta.audioEffects,
              item.duration
            )
          }
        }))
      })
    )
  }, [])

  const _handleLeftResize = useCallback(
    (newWidth: number) => {
      handleLeftResize(itemId, newWidth)
    },
    [handleLeftResize, itemId]
  )

  const _handleRightResize = useCallback(
    (newWidth: number) => {
      handleRightResize(itemId, newWidth)
    },
    [handleRightResize, itemId]
  )

  return {
    handleLeftResize: _handleLeftResize,
    handleRightResize: _handleRightResize,
    onResizeEnd,
    onResizeStart
  }
}
